import React, { useEffect, useMemo, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';
import { PencilLine, CopySimple, Trash, Plus } from 'phosphor-react';
import { useForm } from 'react-hook-form';
// API & HOOKS
import { useApi } from '../../hooks/UseApi';
import TestRunApi from '../../api/TestRunApi';
import UseQueryProject from '../../hooks/UseQueryProject';
import UseTableColumns from '../../hooks/UseTableColumns';
import UseUserTablePreferences from '../../hooks/UseUserTablePreferences';
import UseTableHelper from '../../hooks/UseTableHelper';
import UseSystemField from '../../hooks/UseSystemField';
import TestRunExecutionApi from '../../api/TestRunExecutionApi';
import { setRefreshData } from '../../context/ProjectActions';
import { useAppDispatch } from '../../context/AppContext';
// COMPONENTS
import Table from '../../components/table/Table';
import { TableColumns } from '../../components/table/TableColumns';
import TestRunModal from './modal/TestRunModal';
import CloneModal from '../../components/clone/CloneModal';
import ConfirmationModal from '../../components/confirm/ConfirmationModal';
import DefectModal from '../defect/Modal/DefectModal';
// DESIGN SYSTEM
import { Dropdown } from '../../designSystem/DesignSystem';
// HELPER & CONSTATNS
import { getPageFilterInput, isValidValue } from '../../helpers/Helper';
import {
  getDefectName,
  getTestCaseName,
  getSuiteName,
  getTestFolderName,
  getCycleName
} from '../../helpers/ArtifactNameHelper';
import { INFO_MESSAGES, showErrorMes, showSuccessMes } from '../../helpers/NotificationHelper';
import {
  DEFAULT_PAGE_PARAMS,
  disableResizingForColumns,
  getNotResizedColumnList,
  getTableActions,
  handleSelectedRow,
  translateColumns
} from '../../helpers/TableHelper';
import { hasPrivilege, isClonePrivileged, isDeletePrivileged } from '../../helpers/PriviligeHelper';
import { TABLE_ACTION_COLUMNS_STYLE } from '../../helpers/ButtonConstants';
import { ENTITY_TYPES, PRIVILEGES, RunExecutionStatus } from '../../constants';
import EnumHelper from '../../helpers/EnumHelper';
import { translateArrItemName } from '../../helpers/TranslationHelper';

const TestRunReactTable = ({ filter, setRefreshStatus, refreshStatus, projectConfig, jiraConfig, users }) => {
  const { control, setValue, getValues } = useForm({ mode: 'onBlur', shouldFocusError: true, criteriaMode: 'all' });
  const { projectId } = UseQueryProject('projectId');
  const { selectedColumns } = UseTableColumns(projectId, ENTITY_TYPES.TEST_RUN);
  const { userSavedFilters } = UseUserTablePreferences(projectId, ENTITY_TYPES.TEST_RUN);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { createTableData } = UseTableHelper();
  UseSystemField(projectId, ENTITY_TYPES.TEST_RUN, 'status');

  const [pageParams, setPageParams] = useState(DEFAULT_PAGE_PARAMS('code', 'DESC'));
  const [customFilters, setCustomFilters] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);

  const [openEditModal, setOpenEditModal] = useState(false);
  const [openCloneModal, setOpenCloneModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openDefectModal, setOpenDefectModal] = useState(false);
  const [rerenderKey, setRerenderKey] = useState(0);

  const getTestRuns = useApi({
    name: 'fetching test runs',
    api: TestRunApi.getAll,
    autoLoad: false,
    onSuccess: () => {
      setPageParams((prevState) => ({
        ...prevState,
        size: getTestRuns?.data.size,
        pageSize: getTestRuns?.data.pageSize,
        totalPages: getTestRuns?.data?.totalPages,
        totalElements: getTestRuns?.data?.totalElements
      }));
      setRefreshStatus(false);
    },
    onError: (err) => showErrorMes(err)
  });

  const deleteTestRun = useApi({
    name: 'delete item',
    api: TestRunApi.delete,
    initialValue: false,
    autoLoad: false,
    onSuccess: () => {
      setOpenDeleteModal(false);
      showSuccessMes(INFO_MESSAGES.delete);
      setRefreshStatus(true);
    },
    onError: (err) => showErrorMes(err)
  });

  const quickExecution = useApi({
    name: 'saving test run',
    api: TestRunExecutionApi.quickExecute,
    onSuccess: () => {
      refreshTable();
      dispatch(setRefreshData(true));
    },
    onError: (err) => showErrorMes(err)
  });

  const tData = useMemo(() => {
    let data = [];
    if (getTestRuns?.data?.content) {
      const columnsConfig = TableColumns[ENTITY_TYPES.TEST_RUN];

      // Defect,Test Case, Test Cycle, Test Folder and Test Suite are a configurable fields, so they should be updated
      const defectColumn = columnsConfig.find((column) => column?.key === 'defectCount');
      if (defectColumn?.name) defectColumn.name = `${getDefectName(projectConfig)}`;

      const testCaseNameColumn = columnsConfig.find((column) => column?.key === 'testCaseName');
      if (testCaseNameColumn?.name)
        testCaseNameColumn.name = `${t('entityName', { entity: getTestCaseName(projectConfig) })} `;

      const testCaseCodeColumn = columnsConfig.find((column) => column?.key === 'testCaseCode');
      if (testCaseCodeColumn?.name)
        testCaseCodeColumn.name = `${t('entityCode', { entity: getTestCaseName(projectConfig) })} `;

      const testCycleNameColumn = columnsConfig.find((column) => column?.key === 'testCycleName');
      if (testCycleNameColumn?.name)
        testCycleNameColumn.name = `${t('entityName', { entity: getCycleName(projectConfig) })} `;

      const testCycleCodeColumn = columnsConfig.find((column) => column?.key === 'testCycleCode');
      if (testCycleCodeColumn?.name)
        testCycleCodeColumn.name = `${t('entityCode', { entity: getCycleName(projectConfig) })} `;

      const testFolderNameColumn = columnsConfig.find((column) => column?.key === 'testFolderName');
      if (testFolderNameColumn?.name)
        testFolderNameColumn.name = `${t('entityName', { entity: getTestFolderName(projectConfig) })} `;

      const testFolderCodeColumn = columnsConfig.find((column) => column?.key === 'testFolderCode');
      if (testFolderCodeColumn?.name)
        testFolderCodeColumn.name = `${t('entityCode', { entity: getTestFolderName(projectConfig) })} `;

      const testSuiteNameColumn = columnsConfig.find((column) => column?.key === 'testSuiteName');
      if (testSuiteNameColumn?.name)
        testSuiteNameColumn.name = `${t('entityName', { entity: getSuiteName(projectConfig) })} `;

      const testSuiteCodeColumn = columnsConfig.find((column) => column?.key === 'testSuiteCode');
      if (testSuiteCodeColumn?.name)
        testSuiteCodeColumn.name = `${t('entityCode', { entity: getSuiteName(projectConfig) })} `;

      data = getTestRuns?.data?.content.map((item) =>
        createTableData(item, columnsConfig, projectId, false, ENTITY_TYPES.TEST_RUN, users)
      );
    }
    return data;
  }, [getTestRuns?.data?.content, getI18n().language]);

  const loading = useMemo(() => getTestRuns?.loading, [getTestRuns?.loading]);

  const handleDelete = () => {
    deleteTestRun.execute(selectedItem?.id);
  };

  const onFilter = (newFilters) => {
    setCustomFilters(newFilters);
    getItems({ ...pageParams }, newFilters);
  };

  const getByPage = (newPageParams) => {
    let pageInput = { ...pageParams, ...newPageParams };
    getItems(pageInput, customFilters);
  };

  const getItems = (pageInput, filterInput) => {
    let fil = { ...filterInput, ...filter };
    if (userSavedFilters?.filterInput) {
      fil = { ...fil, ...userSavedFilters?.filterInput };
    }

    let body = { ...fil, ...getPageFilterInput(pageInput), projectId };
    getTestRuns.execute(body);
  };

  let quickTestRunOptions = translateArrItemName(Object.values(RunExecutionStatus), t);

  const getTableDropdownProps = (code, values) => ({
    control,
    options: quickTestRunOptions || [],
    selectedItem: Object.values(RunExecutionStatus).find((item) => item.text === values[`status-${code}`]),
    name: `status-${code}`,
    key: `status-${code}`,
    passValue: (value) => {
      setValue(`status-${code}`, value[0]?.value);
      handleChangeStatus(value[0]?.value, code);
    }
  });

  const setInitialFormValues = (options) => {
    const { setValue } = options;
    getTestRuns?.data?.content?.forEach((item) => {
      const id = item.id;
      setValue(`status-${id}`, EnumHelper('RunExecutionStatus', item?.lastTestRunExecution?.status));
    });
  };

  const handleChangeStatus = (status, item) => {
    if (status !== 'NOT_EXECUTED' && status !== 'Not Executed') {
      let execution = {
        testRunId: item,
        quickRunExecution: true,
        status
      };
      quickExecution.execute(execution);
    }
  };

  const optionsFn = (row) => {
    return [
      {
        name: t('Edit'),
        icon: (weight) => (
          <PencilLine
            size={TABLE_ACTION_COLUMNS_STYLE.iconSize}
            weight={weight}
          />
        ),
        disabled: false,
        onClick: () => {
          handleSelectedRow(getTestRuns?.data?.content, row, setSelectedItem);
          setOpenEditModal(true);
        }
      },
      ...(isClonePrivileged(ENTITY_TYPES.TEST_RUN, projectId)
        ? [
            {
              name: t('Clone'),
              icon: (weight) => (
                <CopySimple
                  size={TABLE_ACTION_COLUMNS_STYLE.iconSize}
                  weight={weight}
                />
              ),
              disabled: false,
              onClick: () => {
                handleSelectedRow(getTestRuns?.data?.content, row, setSelectedItem);
                setOpenCloneModal(true);
              }
            }
          ]
        : []),
      ...(isDeletePrivileged(ENTITY_TYPES.TEST_RUN, projectId)
        ? [
            {
              name: t('Move To Trash'),
              icon: (weight) => (
                <Trash
                  size={TABLE_ACTION_COLUMNS_STYLE.iconSize}
                  weight={weight}
                />
              ),
              disabled: false,
              onClick: () => {
                handleSelectedRow(getTestRuns?.data?.content, row, setSelectedItem);
                setOpenDeleteModal(true);
              }
            }
          ]
        : []),
      {
        name: `${getDefectName(projectConfig)}`,
        icon: (weight) => (
          <Plus
            size={TABLE_ACTION_COLUMNS_STYLE.iconSize}
            weight={weight}
          />
        ),
        disabled: false,
        onClick: () => {
          handleSelectedRow(getTestRuns?.data?.content, row, setSelectedItem);
          setOpenDefectModal(true);
        }
      }
    ];
  };

  const refreshTable = () => getByPage({});

  let columns = [getTableActions(optionsFn), ...selectedColumns];
  columns = disableResizingForColumns(columns, getNotResizedColumnList(ENTITY_TYPES.TEST_RUN));
  columns = translateColumns(columns, t);

  useEffect(() => {
    if (refreshStatus) {
      refreshTable();
      setRerenderKey((prevKey) => prevKey + 1);
    }
  }, [refreshStatus]);

  useEffect(() => {
    const executionStatus = Object.values(RunExecutionStatus);
    setInitialFormValues({ setValue, executionStatus, getValues });
  }, [getTestRuns?.data?.content]);

  return (
    <>
      <Table
        key={rerenderKey}
        columns={[
          ...columns,
          {
            Header: t('Status'),
            accessor: 'status',
            minWidth: 150,
            width: 250,
            Cell: (cell) => {
              const code = cell.row.original.id;
              return (
                <Dropdown
                  {...getTableDropdownProps(code, getValues())}
                  disabled={!hasPrivilege(projectId, PRIVILEGES.UPDATE_TEST_EXECUTION)}
                />
              );
            }
          }
        ]}
        data={tData}
        pagination
        loading={loading}
        pageParams={pageParams}
        getByPage={getByPage}
        onFilter={onFilter}
        showCheckbox={true}
        entityType={ENTITY_TYPES.TEST_RUN}
        rowHeight={'h-16'}
      />

      {openEditModal && (
        <TestRunModal
          isOpen={openEditModal}
          data={selectedItem}
          closeModal={(form) => {
            setOpenEditModal(false);
            if (isValidValue(form)) setRefreshStatus(true);
          }}
        />
      )}

      {openCloneModal && (
        <CloneModal
          isOpen={openCloneModal}
          closeModal={(data) => {
            setOpenCloneModal(false);
            if (isValidValue(data)) setRefreshStatus(true);
          }}
          data={selectedItem}
          entityType={ENTITY_TYPES.TEST_RUN}
          isBulk={false}
        />
      )}
      {openDeleteModal && (
        <ConfirmationModal
          isOpen={openDeleteModal}
          loading={deleteTestRun?.loading}
          closeModal={() => setOpenDeleteModal(false)}
          handleConfirm={handleDelete}
          header={'Confirm Delete'}
          content={'Do you really want to delete this?'}
        />
      )}
      {openDefectModal && (
        <DefectModal
          testCycleId={selectedItem?.testCycle?.id}
          runId={selectedItem?.id}
          isOpen={openDefectModal}
          closeModal={() => setOpenDefectModal(false)}
          submitCallBack={() => setRefreshStatus(true)}
          jiraConfig={jiraConfig}
          projectConfig={projectConfig}
        />
      )}
    </>
  );
};
export default TestRunReactTable;
